<script setup>

import { ref, onMounted, reactive } from "vue";
import Paginator from 'primevue/paginator';
import swal from 'sweetalert';
import Tabs from "primevue/tabs";
import TabPanels from "primevue/tabpanels";
import TabPanel from "primevue/tabpanel";
import JobsTable from "../components/JobsTable.vue";
import JobsFailedTable from "../components/JobsFailedTable.vue";
import JobsFilter from "../components/JobsFilter.vue";
import JobsModal from "../components/JobsModal.vue";
import JobsTabList from "../components/JobsTabList.vue";

window.Echo.channel('jobs')
    .listen('.job.processed', function (e) {
        const params = handleSearchParams();
        fetchJobs(params);
    });

const jobs = ref([]);
const jobsFailed = ref([]);
const selectJobs = ref([]);
const activeIndex = ref(0);
const sortOrder = ref(1);
const sortField = ref('');
const loading = ref(false);
const visible = ref(false);

const filter = reactive({
    uuid: null,
    exception: null,
    entity: null,
    data_inicio: null,
    data_fim: null
});

const dadosModal = reactive({
    titulo:'Modal',
    conteudo : ''
});

const paginate = reactive({
    rows: 10,
    total: 0,
    to: 0
})

onMounted(() => {
    getJobs();
})

const getJobs = async ({ page = 1, rows = 10 } = {}) => {
    loading.value = true;

    const params = handleSearchParams({ page, rows });

    if (activeIndex.value === 0) {
        await fetchJobs(params);
    } else if (activeIndex.value === 1) {
        await fetchJobsFailed(params);
    }
}

const fetchJobs = async (params) => {
    try {
        const resp = await axios.get("/api/jobs?"
            + params.toString()
            + `&sortField=${sortField.value}&sortOrder=${sortOrder.value}`
        );
        const data = await resp.data;
        jobs.value = data.data;
        handlePaginate(data);
        loading.value = false;
    } catch (e) {
        loading.value = false;
        swal("Erro", e.response.data.message, "error");
    }
}

const fetchJobsFailed = async (params) => {
    try {
        const resp = await axios.get("/api/jobs/failed?"
            + params.toString()
            + `&sortField=${sortField.value}&sortOrder=${sortOrder.value}`
        );
        const data = await resp.data;
        jobsFailed.value = data.data;
        handlePaginate(data);
        loading.value = false;
    } catch (e) {
        loading.value = false;
        swal("Erro", e.response.data.message, "error");
    }
}

const handleSearchParams = ({ page = 1, rows = 10 } = {}) => {
    const { uuid, exception, entity, data_inicio, data_fim } = filter;
    const search = { uuid, exception, entity, data_inicio, data_fim };

    return new URLSearchParams({
        page: page,
        perPage: rows,
        search: JSON.stringify(search)
    });
}

const handlePaginate = (data) => {
    paginate.rows = data.per_page;
    paginate.total = data.total;
    paginate.to = data.to;
}

const retry = async () => {
    const failed_uuids = [];

    selectJobs.value.forEach(e => {
        failed_uuids.push(e.uuid);
    });

    loading.value = true;

    try {
        await axios.post("/api/jobs/failed/retry-many", {failed_uuids});
    } catch (e) {
        if (e.response && e.response.status === 400) {
            swal("Erro", e.response.data.message, "error");
        }
    }

    selectJobs.value = [];

    loading.value = false;

    getJobs();
}

const destroy = async () => {
    const failed_uuids = [];

    if (activeIndex.value === 0) {
        selectJobs.value.forEach(e => {
            failed_uuids.push(JSON.parse(e.payload).uuid);
        });
    } else {
        selectJobs.value.forEach(e => {
            failed_uuids.push(e.uuid);
        });
    }

    loading.value = true;

    try {
        const isFailed = activeIndex.value === 1 ? "failed/" : "";
        await axios.post(`/api/jobs/${isFailed}destroy-many`, {failed_uuids});
    } catch (e) {
        swal("Erro", e.response.data.message, "error");
    }

    selectJobs.value = [];

    loading.value = false;

    getJobs();
}

const formatDate = (data) => {
    let date;

    if (Number.isInteger(data)) {
        date = new Date(data * 1000);
    } else if (data.includes("T")) {
        date = new Date(data);
    } else {
        let ISODate = data.replace(" ", "T") + ".000000Z";
        date = new Date(ISODate);
    }

    return date.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    }).replace(",", " ");
}

const sort = (event) => {
    sortField.value = event.sortField;
    sortOrder.value = event.sortOrder;

    getJobs();
}

const showModal = (titulo = '', conteudo = '') => {
    dadosModal.titulo = titulo;
    dadosModal.conteudo = conteudo;
    visible.value = true;
}

const clearFilter = () => {
    filter.uuid = '';
    filter.exception = '';
    filter.entity = '';
    filter.data_fim = '';
    filter.data_inicio = '';
    getJobs();
}

const clearSort = () => {
    sortField.value = '';
    sortOrder.value = 1;
}

const trataHtml = (text) =>{
    return text.split("#").join("<br>#");
}

</script>

<template>
    <JobsModal v-model:visible="visible" :dadosModal="dadosModal" :trataHtml="trataHtml" />

    <div class="container-fluid">
        <JobsFilter :filter="filter" :activeIndex="activeIndex" @onSearch="getJobs" @clearFilter="clearFilter" />

        <Tabs value="0" v-model:activeIndex="activeIndex">
            <JobsTabList
                :activeIndex="activeIndex"
                :selectJobs="selectJobs"
                :retry="retry"
                :destroy="destroy"
                :getJobs="getJobs"
                :clearFilter="clearFilter"
                :clearSort="clearSort"
                @update:activeIndex="activeIndex = $event"
                @clearSelectedJobs="selectJobs = []"
            />

            <TabPanels>
                <TabPanel value="0" v-if="jobs.length !== 0">
                    <JobsTable
                        v-model:selectJobs="selectJobs"
                        :jobs="jobs"
                        :showModal="showModal"
                        :formatDate="formatDate"
                        :loading="loading"
                        @onSort="sort"
                    />
                </TabPanel>

                <TabPanel value="1" v-if="jobsFailed.length !== 0">
                    <JobsFailedTable
                        v-model:selectJobs="selectJobs"
                        :jobsFailed="jobsFailed"
                        :showModal="showModal"
                        :formatDate="formatDate"
                        :loading="loading"
                        @onSort="sort"
                    />
                </TabPanel>
            </TabPanels>
        </Tabs>

        <Paginator
            v-if="paginate.total !== 0"
            :rows="paginate.rows"
            :totalRecords="paginate.total"
            :rowsPerPageOptions="[10, 20, 30, 50, 80, 100]"
            @page="(event) => getJobs({ page: event.page + 1, rows: event.rows })"
        >
            <template #start>
                {{ paginate.to }} de {{ paginate.total }}
            </template>
        </Paginator>

        <p v-else class="text-center fs-4">Sua pesquisa não retornou resultados</p>
    </div>
</template>
